"use client";

import { useQuery } from "@tanstack/react-query";
import moment from "moment";

import { ONE_MINUTE_MS } from "#/config/time";
import { useAccount } from "#/store/wallet/account/account";
import { type MarginAccountStatePairsType } from "#/types/markets";
import {
  type PortfolioHistoryActivity,
  type PortfolioHistoryEntry,
} from "#/types/portfolio";
import { InstrumentTypes } from "#/types/trade";
import {
  getOptionInstrumentProperties,
  getPerpInstrumentProperties,
  isOptionInstrument,
} from "#/utils/instrument.utils";
import { getPortfolioSymbolFromPair } from "#/utils/marketPair";

import { fetchAccountHistory } from "../kyan.service";
import {
  type AccountHistoryEvent,
  AccountHistoryRequest,
  type AccountHistoryResponse,
  type FundingEventData,
  type MergedTransferEventData,
  type SettlementEventData,
  type TradeEventData,
  type TransferEventData,
} from "../kyan.types";
import { defaultApiResponseHandler } from "../kyan.utils";

export interface UseAccountHistoryParams {
  limit?: number;
  account?: string;
  startTimestamp?: number;
  endTimestamp?: number;
}

const isTradeEventData = (
  data: AccountHistoryEvent["data"],
): data is TradeEventData => "trade_id" in data;

const isTransferEventData = (
  data: AccountHistoryEvent["data"],
): data is TransferEventData | MergedTransferEventData =>
  "is_internal_transfer" in data;

const isMergedTransferEventData = (
  data: AccountHistoryEvent["data"],
): data is MergedTransferEventData =>
  isTransferEventData(data) &&
  ("from_margin_account" in data || data.action === "internal_transfer");

const isSettlementEventData = (
  data: AccountHistoryEvent["data"],
): data is SettlementEventData =>
  "instrument_name" in data && "fee" in data && !("funding_rate" in data);

const isFundingEventData = (
  data: AccountHistoryEvent["data"],
): data is FundingEventData => "funding_rate" in data;

const transformTradeEvent = (
  event: AccountHistoryEvent,
  data: TradeEventData,
): PortfolioHistoryEntry => {
  const { instrument_name, size, average_price, realized_pnl, liquidation } =
    data;

  const totalTradeFees =
    data.fees.systemic_risk_fee +
    data.fees.taker_fee +
    data.fees.liquidation_fee;

  if (isOptionInstrument(instrument_name)) {
    const { underlying, strike, maturity, optionType } =
      getOptionInstrumentProperties(instrument_name);

    return {
      time: moment(event.timestamp).toISOString(),
      activity: "trade",
      liquidation,
      details: {
        token: underlying,
        strike,
        maturity,
        optionType,
        instrumentType: InstrumentTypes.Options,
      },
      totalTradeFees,
      direction: data.direction,
      size,
      price: average_price,
      pnl: realized_pnl,
      iv: data.iv,
      tradeId: data.trade_id,
      markPrice: data.mark_price,
      spotPrice: data.index_price,
      instrumentName: instrument_name,
      contracts: data.contracts,
    };
  }

  const { underlying } = getPerpInstrumentProperties(instrument_name);

  return {
    time: moment(event.timestamp).toISOString(),
    activity: "trade",
    liquidation,
    details: {
      token: underlying,
      instrumentType: InstrumentTypes.Perps,
    },
    totalTradeFees,
    direction: data.direction,
    size,
    price: average_price,
    pnl: realized_pnl,
    iv: data.iv,
    tradeId: data.trade_id,
    markPrice: data.mark_price,
    spotPrice: data.index_price,
    instrumentName: instrument_name,
    contracts: data.contracts,
  };
};

const transformTransferEvent = (
  event: AccountHistoryEvent,
  data: TransferEventData | MergedTransferEventData,
): PortfolioHistoryEntry => {
  const token = getPortfolioSymbolFromPair(
    data.symbol as MarginAccountStatePairsType,
  );

  const isDeposit = data.action === "deposit";
  const activity: PortfolioHistoryActivity = isDeposit
    ? "deposit"
    : "withdrawal";

  return {
    time: moment(event.timestamp).toISOString(),
    activity,
    details: {
      token,
    },
    size: data.amount,
  };
};

const transformMergedTransferEvent = (
  event: AccountHistoryEvent,
  data: MergedTransferEventData,
): PortfolioHistoryEntry => {
  // Handle new internal transfer structure with from_symbol and to_symbol
  const fromToken = data.from_symbol
    ? getPortfolioSymbolFromPair(
        data.from_symbol as MarginAccountStatePairsType,
      )
    : getPortfolioSymbolFromPair(data.symbol as MarginAccountStatePairsType);

  const toToken = data.to_symbol
    ? getPortfolioSymbolFromPair(data.to_symbol as MarginAccountStatePairsType)
    : fromToken;

  return {
    time: moment(event.timestamp).toISOString(),
    activity: "transfer",
    details: {
      token: fromToken,
      toToken,
    },
    size: data.amount,
  };
};

const transformSettlementEvent = (
  event: AccountHistoryEvent,
  data: SettlementEventData,
): PortfolioHistoryEntry => {
  const { underlying, strike, maturity, optionType } =
    getOptionInstrumentProperties(data.instrument_name);

  return {
    time: moment(event.timestamp).toISOString(),
    activity: "settlement",
    details: {
      token: underlying,
      strike,
      maturity,
      optionType,
      instrumentType: InstrumentTypes.Options,
    },
    fee: data.fee,
    pnl: data.realized_pnl,
    instrumentName: data.instrument_name,
  };
};

const transformFundingEvent = (
  event: AccountHistoryEvent,
  data: FundingEventData,
): PortfolioHistoryEntry => {
  const token = getPortfolioSymbolFromPair(
    data.symbol as MarginAccountStatePairsType,
  );

  return {
    time: moment(event.timestamp).toISOString(),
    activity: "funding",
    details: {
      token,
      instrumentType: InstrumentTypes.Perps,
    },
    pnl: data.realized_pnl,
    direction: data.direction === "long" ? "buy" : "sell",
    spotPrice: data.spot_price,
    fundingRate: data.funding_rate,
    instrumentName: data.instrument_name,
  };
};

const transformEvent = (
  event: AccountHistoryEvent,
): PortfolioHistoryEntry | null => {
  const { data, event_type } = event;

  switch (event_type) {
    case "trade":
      if (isTradeEventData(data)) {
        return transformTradeEvent(event, data);
      }
      break;

    case "transfer":
      if (isMergedTransferEventData(data)) {
        return transformMergedTransferEvent(event, data);
      }

      if (isTransferEventData(data)) {
        return transformTransferEvent(event, data);
      }
      break;

    case "settlement":
      if (isSettlementEventData(data)) {
        return transformSettlementEvent(event, data);
      }
      break;

    case "funding":
      if (isFundingEventData(data)) {
        return transformFundingEvent(event, data);
      }
      break;
  }

  return null;
};

const transformAccountHistory = (
  response: AccountHistoryResponse,
): {
  entries: PortfolioHistoryEntry[];
  totalCount: number;
  nextCursor: string | null;
} => {
  const { events, total_count, next_cursor } = response;

  const entries = events
    .map(transformEvent)
    .filter(Boolean) as PortfolioHistoryEntry[];

  return {
    entries,
    totalCount: total_count || entries.length,
    nextCursor: next_cursor,
  };
};

export const useAccountHistory = ({
  address,
  ...payload
}: Partial<AccountHistoryRequest> = {}) => {
  const { account: currentAccount } = useAccount();

  const effectiveAccount = address || currentAccount;

  const {
    data = { entries: [], totalCount: 0, nextCursor: null },
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["fetchAccountHistory", effectiveAccount, payload],
    queryFn: () =>
      defaultApiResponseHandler({
        serviceName: "Fetch Account History",
        payload: {
          address: effectiveAccount!,
          ...payload,
          // TODO: remove once backend defaults to returning all history if no timestamps provided @mariolugo
          start_timestamp: 1767222000000, // Start from Jan 1, 2026
        },
        service: fetchAccountHistory,
      }),
    select: transformAccountHistory,
    enabled: Boolean(effectiveAccount),
    refetchInterval: 5 * ONE_MINUTE_MS, // 5 minutes
  });

  return {
    data: data.entries,
    totalCount: data.totalCount,
    nextCursor: data.nextCursor,
    isLoading,
    isFetching,
    refetch,
  };
};
